import React from 'react';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import reportWebVitals from './reportWebVitals';
import theme from './theme';
import {
  createRoutesFromChildren,
  matchRoutes,
  BrowserRouter as Router,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import Auth0ProviderWithNavigate from './auth/auth0-provider-with-navigate';
import App from './App';
import './index.css';
import { UserProvider } from './hooks/useUser';
import { ErrorBoundary } from '@sentry/react';
import FullAppFallback from './components/fallbacks/FullAppFallback';
import { AgencyProvider } from './hooks/useAgency';
import { InfoProvider } from './hooks/useInfo';

// more configuration will be avilable with React 19, so check it out when we update.
// if we end up adding redux or tanstack, we can also configure that with Sentry.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_URL,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 1.0,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.consoleLoggingIntegration({ levels: ['log', 'error', 'warn'] })
  ],
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  _experiments: { enableLogs: true }
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <Router>
      <ThemeProvider theme={theme}>
        <ErrorBoundary fallback={<FullAppFallback />}>
          <Auth0ProviderWithNavigate>
            <UserProvider>
              <AgencyProvider>
                <InfoProvider>
                  <App />
                </InfoProvider>
              </AgencyProvider>
            </UserProvider>
          </Auth0ProviderWithNavigate>
        </ErrorBoundary>
      </ThemeProvider>
    </Router>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
